import { ICellEditorComp, ICellEditorParams } from "ag-grid-community";
import { includes } from "lodash";
import React, { ChangeEventHandler, Component, RefObject } from "react";

interface IProps extends ICellEditorParams {
  min?: number;
  max?: number;
  integer?: boolean;
}

interface IState {
  value: string;
}

export default class NumericCellEditor extends Component<IProps, IState>
  implements ICellEditorComp {
  private readonly cancelBeforeStart: boolean;
  private readonly inputRef: RefObject<HTMLInputElement>;
  private readonly validChars: string;

  constructor(props: IProps) {
    super(props);

    this.validChars = props.integer ? "-1234567890" : "-1234567890.";
    this.cancelBeforeStart = (props.charPress &&
      this.validChars.indexOf(props.charPress) < 0) as boolean;

    let value = props.value.toString();
    if (props.keyPress === 8 || props.keyPress === 46) value = "";
    else if (props.charPress) value = props.charPress;

    this.state = { value };
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    const { current } = this.inputRef;
    current && current.addEventListener("keydown", this.onKeyDown);
  }

  componentWillUnmount() {
    const { current } = this.inputRef;
    current && current.removeEventListener("keydown", this.onKeyDown);
  }

  afterGuiAttached() {
    // get ref from React component
    const eInput = this.inputRef.current;

    if (eInput) {
      eInput.focus();

      // when we started editing, we want the caret at the end, not the start.
      // this comes into play in two scenarios: a) when user hits F2 and b)
      // when user hits a printable character, then on IE (and only IE) the caret
      // was placed after the first character, thus 'apple' would end up as 'pplea'
      const length = eInput.value ? eInput.value.length : 0;
      if (length > 0) {
        eInput.setSelectionRange(length, length);
      }
    }
  }

  getGui() {
    return this.inputRef.current as HTMLInputElement;
  }

  getValue() {
    const { value } = this.state;
    switch (value) {
      case "0":
      case "-":
        return 0;
      default:
        return parseFloat(value);
    }
  }

  isCancelBeforeStart() {
    return this.cancelBeforeStart;
  }

  isCancelAfterEnd() {
    const value = this.getValue();
    const { integer, min, max } = this.props;
    return (
      (min !== undefined && value < min) || // min value violation
      (max !== undefined && value > max) || // max value violation
      (integer !== undefined && !Number.isInteger(value)) // non-integer violation
    );
  }

  onKeyDown = (event: KeyboardEvent) => {
    if (
      includes(["ArrowLeft", "ArrowRight", "Delete", "Backspace"], event.code)
    ) {
      event.stopPropagation();
      return;
    }

    if (this.validChars.indexOf(event.key) < 0) {
      event.preventDefault();
    }
  };

  handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    this.validateAndChange(event.target.value);
  };

  private validateAndChange(input: string) {
    const { integer = false } = this.props;
    input = input.trim();

    if (input === "") {
      this.setState({ value: "" });
      return;
    }

    if (integer) {
      // int checks
      if (/^-0?$/.test(input)) {
        this.setState({ value: input });
        return;
      } else if (!/^-?\d+$/) {
        return;
      }
    } else {
      // float checks
      if (/^-(?:0?\.0*)?$/.test(input) || /^0?\.0*$/) {
        this.setState({ value: input });
        return;
      } else if (!/^-?\d*(?:\.\d*)?$/.test(input)) {
        return; // check is numeric
      }
    }

    const value = parseFloat(input);

    input = input.endsWith(".") ? input : value.toString();
    this.setState({ value: input });
  }

  render() {
    return (
      <input
        ref={this.inputRef}
        value={this.state.value}
        onChange={this.handleChange}
        style={{ width: "100%" }}
      />
    );
  }
}
